//@flow
import { useCallback, useMemo, useContext } from "react";
import { useSelector } from "react-redux";
import { ArrowNavigationContext } from "data/context";
import { docsTotalCountSelector } from "data/reducers/selectors";
import { type ArrowNavigationSetter, type PaginationProps } from "data/types";
import { DEFAULT_ARROWNAVIGATION_CONTEXT } from "./utils";

export const useDocListPagination = ({
    currentRow,
    setCurrentRow,
    multiple,
    activeRows,
}): PaginationProps => {
    const { setTarget, setCurrentIndex, setRows }: ArrowNavigationSetter =
        useContext(ArrowNavigationContext) || DEFAULT_ARROWNAVIGATION_CONTEXT;

    const result = useSelector((state) => state.docs);
    const totalCount = useSelector(docsTotalCountSelector);
    const setCurrent = useCallback(
        (rowIndex) => {
            setCurrentIndex(rowIndex);
            setCurrentRow(rowIndex);
            return rowIndex;
        },
        [setCurrentIndex, setCurrentRow]
    );

    const getActiveRowItemUri = useCallback(
        (index: number) => {
            const row = result?.rows[index];
            return row?.[row.length - 1] ?? undefined;
        },
        [result.rows]
    );

    const handleRowsUpdate = useCallback(
        (result) => {
            if (result.totalCount === 0) return;
            const documents = {};
            Object.keys(result.rows).forEach((i) => {
                const index = parseInt(i, 10);
                if (
                    !multiple ||
                    Object.keys(activeRows ?? {}).length === 0 ||
                    activeRows?.hasOwnProperty(i)
                ) {
                    const row = result.rows[i];
                    documents[index] =
                        row?.[row.length - 1] ??
                        /* istanbul ignore next */ undefined;
                }
            });
            setRows({
                ...documents,
                length: result.totalCount,
            });
        },
        [activeRows, setRows, multiple]
    );

    const getProps = useCallback(
        (activeRowIndex) => {
            handleRowsUpdate(result);
            return {
                activeRowIndex: activeRowIndex ?? currentRow,
                totalCount,
                onSetActiveRowIndex: setCurrent,
                getActiveRowItemUri,
                setTarget,
            };
        },
        [
            handleRowsUpdate,
            result,
            currentRow,
            totalCount,
            setCurrent,
            getActiveRowItemUri,
            setTarget,
        ]
    );
    return useMemo(
        () => ({
            getProps,
            setCurrent,
            handleRowsUpdate,
        }),
        [getProps, setCurrent, handleRowsUpdate]
    );
};
